import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { filtersLibrary } from "@/components/filter/filter.types.ts";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { FormResultForEmployeeModal } from "@/components/form-result/result-modal/FormResultForEmployeeModal.tsx";
import { FormParticipantStatusTag } from "@/components/forms/components/FormParticipantStatusTag.tsx";
import { Search } from "@/components/search/Search.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { useSurveysHook } from "@/hooks/use-surveys.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useSearchEmployeeSurveyParticipantViews } from "@/queries/employees/use-employee-survey.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiFormParticipantStatus, ApiFormType } from "@/types/forms/form.types.ts";
import {
  ApiSurveyParticipantView,
  ApiSurveyParticipantViewFilter,
  ApiSurveyStatus,
  ApiSurveyType,
} from "@/types/survey.types.ts";
import {
  Box,
  ButtonLink,
  DotSeparator,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  ListItem,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  Text,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "react-use";

export type SurveyParticipantViewsMode = "requests" | "history";

const getFilterByMode = (mode: SurveyParticipantViewsMode): ApiSurveyParticipantViewFilter => {
  if (mode === "requests") {
    return {
      statuses: [ApiSurveyStatus.running],
      participant_statuses: [
        ApiFormParticipantStatus.added,
        ApiFormParticipantStatus.invited,
        ApiFormParticipantStatus.in_progress,
      ],
    };
  }

  return {
    participant_statuses: [ApiFormParticipantStatus.completed],
  };
};

export const SurveysParticipantViewsPage = ({ mode }: { mode: SurveyParticipantViewsMode }) => {
  const { t } = useTranslation();
  const { renderDate, formatPeriodDates, renderEmployeeName } = useFormatter();
  const library = filtersLibrary();
  const [, setSearchParams] = useSearchParams();
  const { openModal } = useModals();
  const { data: employeeMe } = useEmployeeMe();
  const employeeMeId = employeeMe?.id;
  const { getSurveyStatusTag } = useSurveysHook();

  const {
    filter: { filter: baseFilter },
  } = useAppFilter<ApiSurveyParticipantViewFilter>({
    filters: [library.page_number, library.text],
    source: mode === "requests" ? "Survey requests" : "Survey history",
  });
  const [searchTerm, setSearchTerm] = useState(baseFilter.text ?? "");

  const filter = useMemo(() => ({ ...baseFilter, ...getFilterByMode(mode) }), [baseFilter, mode]);

  const {
    data: surveyParticipantViews,
    isLoading,
    isFetching,
  } = useSearchEmployeeSurveyParticipantViews({
    employeeId: employeeMe?.id || 0,
    filter,
    enabled: employeeMe?.id != undefined,
  });

  const pageTitle = mode === "requests" ? t("common:requests") : t("surveys:survey_history");
  const isRequestsMode = mode === "requests";

  useTitle(pageTitle);

  useDebounce(
    () => {
      setSearchParams((searchParams) => {
        if (searchTerm) searchParams.set(library.text.searchParamName, searchTerm);
        else searchParams.delete(library.text.searchParamName);
        return searchParams;
      });
    },
    300,
    [library.text.searchParamName, searchTerm, setSearchParams]
  );

  const handleViewOwnSurveyResponse = useCallback(
    (item: ApiSurveyParticipantView) => {
      if (!employeeMeId) return;

      openModal(FormResultForEmployeeModal, {
        employeeId: employeeMeId,
        questionnaireId: item.survey.id,
        type: ApiFormType.survey,
      });

      analyticsService.trackEvent(analyticEvents.surveys.participantResultViewed, {
        [analyticProperties.id]: employeeMeId,
        [analyticProperties.source]: item.survey.id,
      });
    },
    [employeeMeId, openModal]
  );

  const columns = useMemo<ColumnDef<ApiSurveyParticipantView>[]>(() => {
    const inner: ColumnDef<ApiSurveyParticipantView>[] = [
      {
        id: "survey",
        header: t("surveys:survey"),
        cell: (cell) => (
          <ListItem
            emoji={cell.row.original.survey.emoji}
            titleSlot={
              <Stack direction={"horizontal"} gap={"xs"} className={"truncate text-title"}>
                <DotSeparator>
                  <Text className={"truncate"}>{cell.row.original.survey.title}</Text>
                  {cell.row.original.target_employee && (
                    <Text className={"truncate"}>
                      ({renderEmployeeName(cell.row.original.target_employee)})
                    </Text>
                  )}
                </DotSeparator>
              </Stack>
            }
            forceShowAvatar={true}
            truncate={true}
            behavior={"card"}
          />
        ),
        ...ColumnSizes.employee,
      },
      {
        id: "status",
        header: t("common:status"),
        cell: (cell) => (
          <Stack direction={"horizontal"}>
            {cell.row.original.participant_status ? (
              <FormParticipantStatusTag status={cell.row.original.participant_status} />
            ) : (
              <Text>-</Text>
            )}
          </Stack>
        ),
        ...ColumnSizes.status,
      },
      {
        id: "survey_status",
        header: t("surveys:survey_status"),
        cell: (cell) => (
          <Stack direction={"horizontal"}>
            {getSurveyStatusTag(cell.row.original.survey.status)}
          </Stack>
        ),
        ...ColumnSizes.status,
      },
      {
        id: "period",
        header: t("common:period"),
        cell: (cell) => {
          const start = cell.row.original.survey.date_starts_at;
          const end = cell.row.original.survey.date_ends_at;

          if (start && end) {
            return (
              <TableCell variant={"secondary"}>
                {formatPeriodDates({ dateStart: start, dateEnd: end })?.title}
              </TableCell>
            );
          }

          if (start) {
            return <TableCell variant={"secondary"}>{renderDate(start)} -</TableCell>;
          }

          if (end) {
            return <TableCell variant={"secondary"}>- {renderDate(end)}</TableCell>;
          }

          return <TableCell variant={"secondary"}>-</TableCell>;
        },
        ...ColumnSizes.standard,
      },
    ];

    if (isRequestsMode) {
      inner.push({
        id: "actions",
        header: "",
        cell: (cell) => (
          <Box className={"flex justify-end"}>
            <ButtonLink
              href={`/surveys/${cell.row.original.survey.id}/respond`}
              variant={"secondary"}
            >
              {cell.row.original.participant_status === ApiFormParticipantStatus.in_progress
                ? t("common:continue")
                : cell.row.original.survey.type === ApiSurveyType.survey_with_approval
                  ? t("surveys:request_fill")
                  : t("surveys:start_survey")}
            </ButtonLink>
          </Box>
        ),
        ...ColumnSizes.actions,
      });
    }

    return inner;
  }, [
    formatPeriodDates,
    getSurveyStatusTag,
    isRequestsMode,
    openModal,
    renderDate,
    renderEmployeeName,
    t,
  ]);

  const renderRowAction = useCallback(
    (row: Row<ApiSurveyParticipantView>) => {
      const item = row.original;
      if (item.survey.status !== ApiSurveyStatus.completed || !item.acl.can_view) {
        return null;
      }
      return (
        <DropdownMenu>
          <DropdownMenuItem
            onClick={() => handleViewOwnSurveyResponse(item)}
            title={t("common:view")}
          />
        </DropdownMenu>
      );
    },
    [handleViewOwnSurveyResponse, t]
  );

  return (
    <Page className="h-full">
      <Page.Header title={pageTitle} />
      <Page.Content stretch>
        <Page.StickyLayout
          toolbar={
            <Search
              value={searchTerm}
              onInput={(e) => {
                setSearchTerm(e.currentTarget.value);
              }}
              type={"text"}
              placeholder={t("common:search_by_name")}
              className={"w-96"}
            />
          }
          footer={
            <PaginationBlock
              metadata={surveyParticipantViews?.metadata}
              onPageChange={(page) => {
                setSearchParams((searchParams) => {
                  searchParams.set("page", page.toString());
                  return searchParams;
                });
              }}
            />
          }
        >
          <Table
            stickyHeader={true}
            renderRowAction={isRequestsMode ? undefined : renderRowAction}
            onRowClick={
              isRequestsMode
                ? undefined
                : (row) => {
                    if (
                      row.original.survey.status === ApiSurveyStatus.completed &&
                      row.original.acl.can_view
                    ) {
                      handleViewOwnSurveyResponse(row.original);
                    }
                  }
            }
            data={surveyParticipantViews?.items || []}
            columns={columns}
            showSkeleton={isLoading}
            isFetching={isLoading || isFetching}
            showNotFoundError={!!filter.text && surveyParticipantViews?.metadata.total_items == 0}
            notFoundComponent={<EmptyStateNothingFound />}
            emptyStateComponent={
              <EmptyStateBase
                emoji={":memo:"}
                title={
                  isRequestsMode
                    ? t("surveys:employee_surveys_empty_state_title")
                    : t("surveys:survey_history_empty_state_title")
                }
                description={
                  isRequestsMode
                    ? t("surveys:employee_surveys_empty_state_description")
                    : t("surveys:survey_history_empty_state_description")
                }
              />
            }
          />
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
};
